import * as Sentry from '@sentry/react';
import { Replay } from '@sentry/replay';
import { hasAnalyticsConsent } from './cookieConsent';
import { logger } from './logger';
import {
  safeSessionStorageGetItem,
  safeSessionStorageSetItem,
  safeSessionStorageGetJSON
} from './storageHelper';

// Type definition for UTM parameters
interface UTMParams {
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  gclid?: string | null;
}

let initialized = false;

/**
 * Initialize analytics with Basic Consent Mode 
 * Since GTM is now loaded in HTML, this just handles consent and Sentry
 */
export function initializeAnalytics(): void {
  if (initialized) return;
  
  // Check if user has previously consented
  const userHasConsent = hasAnalyticsConsent();
  
  if (userHasConsent) {
    enableAnalytics();
  }
  
  initialized = true;
  window.__analyticsLoaded = true;
}

/**
 * Enable analytics (called when user has consent)
 */
function enableAnalytics(): void {
  try {
    // Initialize Sentry when analytics are enabled
    initializeSentry();
    
    // Update consent state to granted (GTM is already loaded from HTML)
    updateConsentToGranted();
    
  } catch (error) {
    logger.error('❌ Failed to enable analytics:', error);
  }
}

/**
 * Update consent to granted (Basic Consent Mode)
 */
function updateConsentToGranted(): void {
  if (window.gtag) {
    window.gtag('consent', 'update', {
      'ad_user_data': 'granted',
      'ad_personalization': 'granted',
      'ad_storage': 'granted',
      'analytics_storage': 'granted'
    });
  } else {
    // GTM should be loaded from HTML, but give it a moment just in case
    setTimeout(() => {
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'ad_user_data': 'granted',
          'ad_personalization': 'granted',
          'ad_storage': 'granted',
          'analytics_storage': 'granted'
        });
      } else {
        logger.error('❌ gtag still not available after delay');
      }
    }, 1000);
  }
}

/**
 * Initialize Sentry with Replay for analytics
 */
function initializeSentry(): void {
  try {
    if (process.env.REACT_APP_SENTRY_DSN) {
      Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        integrations: [
          new Replay({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ],
        enabled: process.env.NODE_ENV === 'production',
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
      });
    }
  } catch (error) {
    logger.error('❌ Failed to initialize Sentry:', error);
  }
}

/**
 * Load analytics (called when user accepts analytics cookies)
 * This is the main function called by your cookie banner
 */
export function loadAnalytics(): void {
  if (!initialized) {
    initializeAnalytics();
  }
  
  // Enable analytics if not already done
  if (hasAnalyticsConsent()) {
    enableAnalytics();
  }
}

/**
 * Revoke analytics consent (called when user declines analytics cookies)
 */
export function revokeAnalyticsConsent(): void {
  if (window.gtag) {
    window.gtag('consent', 'update', {
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'ad_storage': 'denied',
      'analytics_storage': 'denied'
    });
  }
}

/**
 * Capture and store UTM parameters from URL
 */
export function captureUTMParams(): void {
  const params = new URLSearchParams(window.location.search);
  const utmData: Record<string, string | null> = {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
    utm_term: params.get('utm_term'),
    gclid: params.get('gclid'), // Google Click ID for enhanced conversions
  };

  // Store if any UTM params exist
  if (Object.values(utmData).some(v => v)) {
    // Use safe storage helpers - will fail gracefully if storage is unavailable
    safeSessionStorageSetItem('utm_params', JSON.stringify(utmData));
    safeSessionStorageSetItem('landing_timestamp', Date.now().toString());
  }
}

/**
 * Track micro-conversions with campaign attribution
 */
export function trackMicroConversion(eventName: string, value?: number): void {
  if (!hasAnalyticsConsent() || !window.gtag) return;

  // Use safe storage helper with fallback to empty object
  const utmParams = safeSessionStorageGetJSON<UTMParams>('utm_params', {});

  window.gtag('event', eventName, {
    value: value || 1,
    currency: 'EUR',
    campaign_source: utmParams.utm_source || 'direct',
    campaign_name: utmParams.utm_campaign || 'none',
  });
}

/**
 * Track GA4 sign_up event for trial signup
 */
export function trackTrialSignup(userEmail: string, organizationName: string): void {
  const hasConsent = hasAnalyticsConsent();

  if (!hasConsent) {
    return;
  }

  if (!window.gtag) {
    setTimeout(() => {
      trackTrialSignup(userEmail, organizationName);
    }, 2000);
    return;
  }

  try {
    // Get UTM params from session storage with safe fallback
    const utmParams = safeSessionStorageGetJSON<UTMParams>('utm_params', {});
    const landingTimestamp = safeSessionStorageGetItem('landing_timestamp');

    // Calculate time to signup (in seconds)
    const timeToSignup = landingTimestamp ?
      Math.floor((Date.now() - parseInt(landingTimestamp)) / 1000) : undefined;

    // Prepare event data with campaign attribution
    const eventData = {
      method: 'trial',
      organization_name: organizationName,
      user_email_domain: userEmail.split('@')[1],
      value: 1,
      currency: 'USD',
      // Campaign attribution
      campaign_source: utmParams.utm_source || 'direct',
      campaign_medium: utmParams.utm_medium || 'none',
      campaign_name: utmParams.utm_campaign || 'none',
      campaign_content: utmParams.utm_content || undefined,
      campaign_term: utmParams.utm_term || undefined,
      gclid: utmParams.gclid || undefined,
      // Additional conversion data
      time_to_signup: timeToSignup,
      landing_page: window.location.pathname
    };

    // Send the standard GA4 sign_up event
    window.gtag('event', 'sign_up', eventData);

    // Also send custom conversion event for Google Ads
    window.gtag('event', 'trial_signup_completed', {
      ...eventData,
      // Enhanced conversions with hashed email
      user_data: {
        email: userEmail.toLowerCase(), // Google will hash this automatically
      }
    });

  } catch (error) {
    logger.error('❌ Error tracking GA4 sign_up event:', error);
  }
}

/**
 * Track other GA4 events
 */
export function trackConversion(eventName: string, eventData?: Record<string, any>): void {
  if (!hasAnalyticsConsent() || !window.gtag) {
    return;
  }

  try {
    window.gtag('event', eventName, {
      ...eventData
    });
  } catch (error) {
    logger.error(`❌ Error tracking GA4 event ${eventName}:`, error);
  }
}

declare global {
  interface Window {
    __analyticsLoaded?: boolean;
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    google_tag_manager?: any;
  }
} 